import type { FC, ReactNode } from 'react'
import type { ContentBlock, ListItem } from '@/types/content'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionIntro } from '@/components/generals/section-intro'
import { cms, hasValue, isPlaceholder, padNumber } from '@/lib/cms'
import { reveal } from '@/lib/reveal'

type Surface = 'bone' | 'paper' | 'stone' | 'ink'

interface FeatureListSectionProps {
    id: string
    block?: ContentBlock
    items: Array<ListItem>
    // grid: numbered columns, rows: one item per line with title and text side by side
    variant?: 'grid' | 'rows'
    columns?: 2 | 3 | 4
    surface?: Surface
    className?: string
}

const surfaces: Record<Surface, string> = {
    bone: 'bg-bone text-ink',
    paper: 'bg-paper text-ink',
    stone: 'bg-stone text-ink',
    ink: 'bg-ink text-bone'
}

const gridColumns: Record<2 | 3 | 4, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4'
}

// A content block followed by its list items (principles, reasons, qualifications ...).
export const FeatureListSection: FC<FeatureListSectionProps> = ({
    id,
    block,
    items,
    variant = 'grid',
    columns = 3,
    surface = 'bone',
    className
}): ReactNode => {
    if (!block && items.length === 0) return null

    const dark = surface === 'ink'
    const line = dark ? 'border-ink-line' : 'border-ink/15'
    const headingId = `${id}-titel`

    return (
        <section id={id} aria-labelledby={headingId} className={cn('relative overflow-hidden py-24 sm:py-32 lg:py-40', surfaces[surface], className)}>
            {dark && <div aria-hidden='true' className='grain-layer' />}

            <Container className={cn('relative', variant === 'rows' && 'grid gap-14 lg:grid-cols-12 lg:gap-10')}>
                <SectionIntro
                    block={block}
                    headingId={headingId}
                    size='lg'
                    tone={dark ? 'dark' : 'light'}
                    className={variant === 'rows' ? 'lg:col-span-5' : 'max-w-3xl'}
                />

                {variant === 'grid' && items.length > 0 && (
                    <ul className={cn('mt-16 grid gap-x-10 gap-y-12 lg:mt-24', gridColumns[columns])}>
                        {items.map((item, index) => (
                            <li key={item.id} className={cn('flex flex-col gap-4 border-t pt-6', line)} {...reveal((index % columns) * 90)}>
                                <span aria-hidden='true' className={cn('type-label', dark ? 'text-ember-bright' : 'text-ember')}>
                                    {padNumber(index)}
                                </span>

                                <h3
                                    data-mdlfy-cms={cms('listenpunkte', item.id, 'titel')}
                                    className={cn('type-display text-display-sm', isPlaceholder(item.titel) && 'is-placeholder self-start')}
                                >
                                    {item.titel}
                                </h3>

                                {hasValue(item.text) && (
                                    <p
                                        data-mdlfy-cms={cms('listenpunkte', item.id, 'text')}
                                        className={cn('max-w-sm text-[0.9375rem] leading-relaxed', dark ? 'text-ash' : 'text-smoke', isPlaceholder(item.text) && 'is-placeholder self-start')}
                                    >
                                        {item.text}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {variant === 'rows' && items.length > 0 && (
                    <ul className={cn('border-t lg:col-span-7', line)}>
                        {items.map((item, index) => (
                            <li
                                key={item.id}
                                className={cn('grid gap-2 border-b py-6 sm:grid-cols-[3rem_1fr] sm:gap-6', line)}
                                {...reveal(index * 80)}
                            >
                                <span aria-hidden='true' className={cn('type-label pt-1', dark ? 'text-ember-bright' : 'text-ember')}>
                                    {padNumber(index)}
                                </span>

                                <div className='flex flex-col items-start gap-2'>
                                    <h3
                                        data-mdlfy-cms={cms('listenpunkte', item.id, 'titel')}
                                        className={cn('text-xl font-semibold tracking-[-0.01em]', isPlaceholder(item.titel) && 'is-placeholder')}
                                    >
                                        {item.titel}
                                    </h3>

                                    {hasValue(item.text) && (
                                        <p
                                            data-mdlfy-cms={cms('listenpunkte', item.id, 'text')}
                                            className={cn('text-[0.9375rem] leading-relaxed', dark ? 'text-ash' : 'text-smoke', isPlaceholder(item.text) && 'is-placeholder')}
                                        >
                                            {item.text}
                                        </p>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Container>
        </section>
    )
}
